import { Alert, Button, Group, Loader, Modal, Text, Textarea } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowBackUp,
  IconArrowLeft,
  IconDoorExit,
  IconFileInvoice,
  IconFileTypePdf,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import { LinkTrail } from 'src/Components/EntityLinks/LinkTrail';
import {
  EXIT_REASONS,
  exitReasonLabel,
  formatEuros,
  formatExitDate,
} from 'src/Components/Inventory/inventoryLabels';
import {
  openInventoryPdf,
  useDeleteInventoryItem,
  useExitInventoryItem,
  usePutBackInventoryItem,
} from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory, { useFetchInventoryItem } from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { inventoryApi } from 'src/Services/API';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { InventoryExitReason, InventoryValueSource } from 'src/Services/API/generated';
import {
  trackInventoryExported,
  trackInventoryItemDeleted,
  trackInventoryItemExited,
} from 'src/Services/Mixpanel/events';

import styles from './InventoryItemPage.module.scss';

/** An object of the house: photos, values (managers), invoices and everything linked to it. */
const InventoryItemPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const { itemId } = useParams<{ itemId: string }>();
  const { data: item, isLoading, isError } = useFetchInventoryItem(itemId);
  const { data: inventory } = useFetchInventory();
  const { mutate: putBack, isLoading: isPuttingBack } = usePutBackInventoryItem(itemId ?? '');
  const { mutate: deleteItem } = useDeleteInventoryItem();
  const [exitOpened, setExitOpened] = useState(false);
  const [exporting, setExporting] = useState(false);

  if (isLoading) return <Loader />;
  if (isError || !item) {
    return <Text>{t('inventory.item.error', 'Impossible de retrouver cet objet.')}</Text>;
  }

  const listPath = `/asset/${assetId}/inventories`;
  const isExited = !!item.exitReason;
  // The author of an object sees its values too; the claim file stays for managers.
  const isManager = inventory?.valuesVisible ?? false;
  const linkedCount = item.invoices.length;

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t('inventory.item.deleteTitle', 'Supprimer {{name}} ?', { name: item.name }),
      children: (
        <div className={styles.modalBody}>
          <Text size="sm">
            {t(
              'inventory.item.deleteText',
              'Supprimer sert à corriger une erreur ou un doublon : la fiche et ses photos disparaissent de l’inventaire. Les factures restent dans Documents, les souvenirs et les actions reliés restent à leur place.'
            )}
          </Text>
          {!isExited && (
            <Alert color="teal">
              {t(
                'inventory.item.deleteInstead',
                'Il a été cassé, donné ou vendu ? Sortez-le plutôt de l’inventaire : il restera consultable.'
              )}
            </Alert>
          )}
        </div>
      ),
      labels: {
        confirm: t('common.delete', 'Supprimer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        deleteItem(item.id, {
          onSuccess: () => {
            trackInventoryItemDeleted({ undone: false });
            navigate(listPath, { state: { deletedItem: { id: item.id, name: item.name } } });
          },
          onError: () => {
            notifications.show({
              color: 'red',
              message: t('inventory.item.deleteError', "L'objet n'a pas pu être supprimé."),
            });
          },
        }),
    });

  const exportItem = async () => {
    if (!assetId) return;
    setExporting(true);
    try {
      const pdf = await openInventoryPdf(
        async () => (await inventoryApi.inventoryControllerExportItem(assetId, item.id)).data
      );
      trackInventoryExported({
        item_count: 1,
        value_to_complete_count: pdf.valueToCompleteCount,
        scope: 'item',
      });
    } catch {
      notifications.show({
        color: 'red',
        message: t('inventory.export.error', "Le PDF n'a pas pu être préparé. Réessayez."),
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Link to={isExited ? `${listPath}/exited` : listPath}>
        <Button variant="transparent" leftSection={<IconArrowLeft size={18} />}>
          {isExited
            ? t('inventory.exited.back', 'Objets sortis')
            : t('inventory.back', "Retour à l'inventaire")}
        </Button>
      </Link>
      <LinkTrail />

      {isExited && (
        <Alert color="orange">
          <div className={styles.exitBanner}>
            <span>
              {t('inventory.item.exitedOn', 'Sorti de l’inventaire le {{date}} · {{reason}}', {
                date: formatExitDate(item.exitDate!),
                reason: exitReasonLabel(t, item.exitReason!),
              })}
            </span>
            {item.exitNote && <span className={styles.note}>{item.exitNote}</span>}
          </div>
        </Alert>
      )}

      {item.photos.length > 0 && (
        <div className={styles.gallery}>
          {item.photos.map(photo => (
            <a key={photo.id} href={photo.url} target="_blank" rel="noreferrer">
              <img src={photo.previewLargeUrl ?? photo.url} alt="" />
            </a>
          ))}
        </div>
      )}

      <header className={styles.header}>
        <h1 className={styles.title}>
          {item.quantity > 1 ? `${item.name} (×${item.quantity})` : item.name}
        </h1>
        <Text className={styles.subtitle}>
          {[
            item.room?.name,
            item.brandAndModel,
            item.purchaseYear
              ? t('inventory.item.boughtIn', 'acheté en {{year}}', { year: item.purchaseYear })
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        {item.condition && <Text className={styles.description}>{item.condition}</Text>}
        {item.notes && <Text className={styles.description}>{item.notes}</Text>}
      </header>

      {item.valuesVisible && (
        <div className={styles.card}>
          <div className={styles.kv}>
            <span>{t('inventory.fields.currentValue', 'Vaut aujourd’hui, à peu près')}</span>
            <b className={item.currentValue === null ? styles.toComplete : undefined}>
              {item.currentValue != null
                ? formatEuros(item.currentValue)
                : t('inventory.toComplete', 'à compléter')}
            </b>
          </div>
          <div className={styles.kv}>
            <span>{t('inventory.fields.replacementValue', 'Valeur à neuf')}</span>
            <b>
              {item.replacementValue != null ? formatEuros(item.replacementValue) : '—'}
              {item.replacementValueSource === InventoryValueSource.Lucien &&
                item.replacementValueSourceUrl && (
                  <a
                    className={styles.source}
                    href={item.replacementValueSourceUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('inventory.item.foundOnline', 'trouvée sur internet')}
                  </a>
                )}
            </b>
          </div>
          <div className={styles.kv}>
            <span>{t('inventory.fields.purchasePrice', "Prix d'achat")}</span>
            <b>{item.purchasePrice != null ? formatEuros(item.purchasePrice) : '—'}</b>
          </div>
          <div className={styles.kv}>
            <span>{t('inventory.item.wear', 'Usure')}</span>
            <b>
              {item.depreciationEnabled
                ? t('inventory.settings.rule', 'Usure : {{rate}} % par an, au plus {{max}} %.', {
                    rate: inventory?.settings.depreciationRatePercent ?? 10,
                    max: inventory?.settings.depreciationMaxPercent ?? 80,
                  })
                : t('inventory.item.noWear', 'Sans usure')}
            </b>
          </div>
          <Text size="sm" c="dimmed">
            {t(
              'inventory.item.indicative',
              'Estimation indicative. En cas de sinistre, l’expert de l’assurance décide.'
            )}
          </Text>
        </div>
      )}

      {linkedCount > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('inventory.add.invoices', 'Factures')}</h2>
          {item.invoices.map(invoice => (
            <a
              key={invoice.id}
              className={styles.invoice}
              href={invoice.url}
              target="_blank"
              rel="noreferrer"
            >
              <IconFileInvoice size={20} />
              <span>{invoice.name}</span>
            </a>
          ))}
        </section>
      )}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('inventory.item.linked', 'Lié à cet objet')}</h2>
        <LinkedEntitiesSection
          entityType={LinkableEntityType.InventoryItem}
          entityId={item.id}
          entityTitle={item.name}
        />
      </section>

      <div className={styles.actions}>
        {isExited ? (
          <Button
            size="md"
            leftSection={<IconArrowBackUp size={20} />}
            loading={isPuttingBack}
            onClick={() =>
              putBack(undefined, {
                onSuccess: () => {
                  notifications.show({
                    color: 'teal',
                    message: t('inventory.item.putBackDone', 'Remis dans l’inventaire.'),
                  });
                },
                onError: () => {
                  notifications.show({
                    color: 'red',
                    message: t('inventory.item.putBackError', "L'objet n'a pas pu être remis."),
                  });
                },
              })
            }
          >
            {t('inventory.item.putBack', 'Remettre dans l’inventaire')}
          </Button>
        ) : (
          <Button
            size="md"
            variant="outline"
            leftSection={<IconDoorExit size={20} />}
            onClick={() => setExitOpened(true)}
          >
            {t('inventory.item.exit', 'Sortir de l’inventaire')}
          </Button>
        )}
        {isManager &&
          isExited &&
          (item.exitReason === InventoryExitReason.LostOrStolen ||
            item.exitReason === InventoryExitReason.Broken) && (
            <Button
              size="md"
              variant="outline"
              leftSection={<IconFileTypePdf size={20} />}
              loading={exporting}
              onClick={exportItem}
            >
              {t('inventory.item.exportForClaim', 'Télécharger la fiche pour la déclaration')}
            </Button>
          )}
        {item.canEdit && (
          <Button
            size="md"
            variant="outline"
            leftSection={<IconPencil size={20} />}
            onClick={() => navigate(`${listPath}/${item.id}/edit`)}
          >
            {t('common.edit', 'Modifier')}
          </Button>
        )}
        {item.canDelete && (
          <Button
            size="md"
            variant="outline"
            color="red"
            leftSection={<IconTrash size={20} />}
            onClick={confirmDelete}
          >
            {t('inventory.item.delete', 'Supprimer l’objet')}
          </Button>
        )}
      </div>

      <ExitModal
        itemId={item.id}
        itemName={item.name}
        isManager={isManager}
        opened={exitOpened}
        onClose={() => setExitOpened(false)}
      />
    </div>
  );
};

/** What happened to it: one of six reasons, today by default, a word if wanted. */
const ExitModal = ({
  itemId,
  itemName,
  isManager,
  opened,
  onClose,
}: {
  itemId: string;
  itemName: string;
  isManager: boolean;
  opened: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<InventoryExitReason | null>(null);
  const [date, setDate] = useState<Date | null>(new Date());
  const [note, setNote] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const { mutate, isLoading } = useExitInventoryItem(itemId);

  const confirm = () => {
    if (!reason) {
      setHint(t('inventory.exit.reasonRequired', 'Choisissez ce qui lui est arrivé.'));
      return;
    }
    mutate(
      {
        reason,
        date: dayjs(date ?? new Date()).format('YYYY-MM-DD'),
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          trackInventoryItemExited({ reason });
          onClose();
        },
        onError: () =>
          setHint(t('inventory.exit.error', "L'objet n'a pas pu être sorti. Réessayez.")),
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={t('inventory.exit.title', 'Sortir {{name}} de l’inventaire', { name: itemName })}
    >
      <div className={styles.modalBody}>
        <Text className={styles.sectionTitle}>
          {t('inventory.exit.question', 'Que lui est-il arrivé ?')}
        </Text>
        <div className={styles.reasons}>
          {EXIT_REASONS.map(value => (
            <button
              key={value}
              type="button"
              aria-pressed={reason === value}
              className={`${styles.reason} ${reason === value ? styles.reasonOn : ''}`}
              onClick={() => {
                setReason(value);
                setHint(null);
              }}
            >
              {exitReasonLabel(t, value)}
            </button>
          ))}
        </div>
        <DateInput
          size="md"
          label={t('inventory.exit.date', 'Date')}
          value={date}
          onChange={setDate}
          maxDate={new Date()}
          valueFormat="D MMMM YYYY"
        />
        <Textarea
          size="md"
          autosize
          minRows={2}
          label={t('inventory.exit.note', 'Un mot')}
          placeholder={t('inventory.exit.notePlaceholder', 'Ex. Vendu au voisin')}
          value={note}
          onChange={event => setNote(event.currentTarget.value)}
        />
        {(reason === InventoryExitReason.Broken || reason === InventoryExitReason.LostOrStolen) && (
          <Alert color="orange">
            {isManager
              ? t(
                  'inventory.exit.claimHint',
                  'Si c’est un sinistre, gardez cette fiche : photos, factures et valeur serviront à votre déclaration. Vous pourrez la télécharger depuis la fiche.'
                )
              : t(
                  'inventory.exit.claimHintMember',
                  'Si c’est un sinistre, prévenez un gestionnaire : la fiche servira à la déclaration.'
                )}
          </Alert>
        )}
        <Text size="sm" c="dimmed">
          {t(
            'inventory.exit.kept',
            'L’objet restera visible dans « Objets sortis », avec ses photos, ses factures et ses liens. Il ne compte plus dans le dossier pour l’assurance.'
          )}
        </Text>
        {hint && <Alert color="red">{hint}</Alert>}
        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button loading={isLoading} onClick={confirm}>
            {t('inventory.item.exit', 'Sortir de l’inventaire')}
          </Button>
        </Group>
      </div>
    </Modal>
  );
};

export default InventoryItemPage;
